/**
 * MVP form builder for tryout evaluation schemas (groups, fields, dnd-kit reorder).
 * Handoff: src/pages/TryoutFormCreationNotes.md
 */
import './FormEditor.css';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import {
  createEmptyGroup,
  FormGroup,
  FormSchema,
  FormTemplate,
  GENERAL_COMMENTS_GROUP_ID,
  getSkillConfig,
  isSkillField,
  normalizeFormSchema,
  schemaHasAllLabels,
} from '../../types/formSchema';
import { generateTemplateId } from '../../utils/ids';
import Button from '../common/Button';
import ConfirmDialog from '../common/ConfirmDialog/ConfirmDialog';
import IconButton from '../common/IconButton/IconButton';
import InlineInputWithIntegratedCheckmark
  from '../common/InlineInputWithIntegratedCheckmark/InlineInputWithIntegratedCheckmark';
import PencilIcon from '../common/PencilIcon';
import FormGroupEditor from './FormGroupEditor';

const MAX_VIDEOS_PER_FORM = 8; // todo, change this back eventually

export interface FormEditorProps {
  /** When provided, the editor opens in edit mode for this template. */
  initial?: FormTemplate;
  onSave: (template: FormTemplate) => void;
  onCancel: () => void;
  /** Dev-only: current draft snapshot for JSON debug panel in parent. */
  onDebugSnapshot?: (template: FormTemplate) => void;
}

function reorderGroupOrders(groups: FormGroup[]): FormGroup[] {
  return groups.map((g, i) => ({ ...g, order: i }));
}

const FormEditor: React.FC<FormEditorProps> = ({
  initial,
  onSave,
  onCancel,
  onDebugSnapshot,
}) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [isEditingName, setIsEditingName] = useState(
    () => !((initial?.name ?? '').trim()),
  );
  const [draftName, setDraftName] = useState(initial?.name ?? '');
  const nameSnapshotRef = useRef(name);
  const [groups, setGroups] = useState<FormGroup[]>(() =>
    normalizeFormSchema({ groups: initial?.schema.groups ?? [] }).groups.filter(
      (g) => g.id !== GENERAL_COMMENTS_GROUP_ID,
    ),
  );
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<string[]>([]);
  const [videoLimitDialogOpen, setVideoLimitDialogOpen] = useState(false);

  useEffect(() => {
    if (!onDebugSnapshot) {
      return;
    }
    const snapshotLabel = (isEditingName ? draftName : name).trim() || 'Untitled Form';
    onDebugSnapshot({
      id: initial?.id ?? '__draft__',
      name: snapshotLabel,
      schema: { groups },
    });
  }, [name, draftName, isEditingName, groups, initial?.id, onDebugSnapshot]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /** Nested field sortables share one DndContext; without filtering, closestCenter
   *  picks field droppables while dragging a section so reorder previews look broken. */
  const formCollisionDetection = useMemo<CollisionDetection>(() => {
    const groupIdSet = new Set(groups.map((g) => g.id));
    const fieldIdToGroupId = new Map<string, string>();
    for (const g of groups) {
      for (const f of g.fields) {
        fieldIdToGroupId.set(f.id, g.id);
      }
    }
    return (args) => {
      const activeId = String(args.active.id);
      if (groupIdSet.has(activeId)) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((c) =>
            groupIdSet.has(String(c.id)),
          ),
        });
      }
      const ownerGroupId = fieldIdToGroupId.get(activeId);
      if (ownerGroupId !== undefined) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (c) => fieldIdToGroupId.get(String(c.id)) === ownerGroupId,
          ),
        });
      }
      return closestCenter(args);
    };
  }, [groups]);

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const activeId = String(active.id);
    const overId = String(over.id);

    const isGroupDrag =
      groups.some((g) => g.id === activeId) &&
      groups.some((g) => g.id === overId);
    if (isGroupDrag) {
      setGroups((prev) => {
        const oldIndex = prev.findIndex((g) => g.id === activeId);
        const newIndex = prev.findIndex((g) => g.id === overId);
        if (oldIndex === -1 || newIndex === -1) return prev;
        return reorderGroupOrders(arrayMove(prev, oldIndex, newIndex));
      });
      return;
    }

    setGroups((prev) => {
      for (const gr of prev) {
        const oldIdx = gr.fields.findIndex((f) => f.id === activeId);
        const newIdx = gr.fields.findIndex((f) => f.id === overId);
        if (oldIdx !== -1 && newIdx !== -1) {
          return prev.map((g) => {
            if (g.id !== gr.id) return g;
            return {
              ...g,
              fields: arrayMove(g.fields, oldIdx, newIdx).map((f, i) => ({
                ...f,
                order: i,
              })),
            };
          });
        }
      }
      return prev;
    });
  };

  const handleAddGroup = (): void => {
    setGroups((prev) => [
      ...prev,
      createEmptyGroup(prev.length),
    ]);
  };

  const handleUpdateGroup = (groupId: string, updated: FormGroup): void => {
    setGroups((prev) => prev.map((g) => (g.id === groupId ? updated : g)));
  };

  const handleDeleteGroup = (groupId: string): void => {
    setGroups((prev) => reorderGroupOrders(prev.filter((g) => g.id !== groupId)));
    setCollapsedGroupIds((prev) => prev.filter((id) => id !== groupId));
  };

  const handleToggleGroupCollapsed = (groupId: string, collapsed: boolean): void => {
    setCollapsedGroupIds((prev) => {
      const alreadyCollapsed = prev.includes(groupId);
      if (collapsed && !alreadyCollapsed) {
        return [...prev, groupId];
      }
      if (!collapsed && alreadyCollapsed) {
        return prev.filter((id) => id !== groupId);
      }
      return prev;
    });
  };

  const beginEditName = (): void => {
    nameSnapshotRef.current = name;
    setDraftName(name);
    setIsEditingName(true);
  };

  const cancelEditName = (): void => {
    setName(nameSnapshotRef.current);
    setDraftName(nameSnapshotRef.current);
    setIsEditingName(false);
  };

  const commitEditName = (): void => {
    const trimmed = draftName.trim();
    if (!trimmed) {
      return;
    }
    setName(trimmed);
    setIsEditingName(false);
  };

  const handleSave = (): void => {
    const trimmedFormName = (isEditingName ? draftName : name).trim();
    const schemaNormalized: FormSchema = normalizeFormSchema({ groups });
    const videoCountAtSave = schemaNormalized.groups.reduce(
      (sum, g) =>
        sum
        + g.fields.filter((f) => isSkillField(f) && getSkillConfig(f).video).length,
      0,
    );
    if (
      !trimmedFormName
      || !schemaHasAllLabels(schemaNormalized)
      || videoCountAtSave > MAX_VIDEOS_PER_FORM
    ) {
      return;
    }
    if (isEditingName) {
      setName(trimmedFormName);
      setDraftName(trimmedFormName);
      setIsEditingName(false);
    }
    const template: FormTemplate = {
      id: initial?.id ?? generateTemplateId(),
      name: trimmedFormName,
      schema: schemaNormalized,
    };
    onSave(template);
  };

  const trimmedName = name.trim();
  const effectiveNameTrimmed = (isEditingName ? draftName : name).trim();
  const videoCount = groups.reduce(
    (sum, g) =>
      sum
      + g.fields.filter((f) => isSkillField(f) && getSkillConfig(f).video).length,
    0,
  );
  const isOverVideoLimit = videoCount > MAX_VIDEOS_PER_FORM;
  const canEnableMoreVideos = videoCount < MAX_VIDEOS_PER_FORM;
  const canSave =
    effectiveNameTrimmed.length > 0
    && schemaHasAllLabels(normalizeFormSchema({ groups }))
    && !isOverVideoLimit;
  const skillCount = groups.reduce(
    (sum, g) => sum + g.fields.filter(isSkillField).length,
    0,
  );

  const groupIds = groups.map((g) => g.id);
  const allGroupsCollapsed = groups.length > 0 && groups.every((group) => (
    collapsedGroupIds.includes(group.id)
  ));
  const toggleAllSectionsLabel = allGroupsCollapsed ? 'Expand All' : 'Collapse All';
  const handleToggleAllSections = (): void => {
    if (groups.length === 0) {
      return;
    }
    setCollapsedGroupIds(
      allGroupsCollapsed
        ? []
        : groups.map((group) => group.id),
    );
  };

  return (
    <div className="fe-editor">
      <div className="fe-editor-top">
        <div className="fe-editor-title-row">
          <h2 className="fe-editor-title">
            {initial ? 'Edit Form' : 'New Tryout Form'}
          </h2>
          <Button
            type="button"
            variant="secondary"
            compact
            onClick={handleToggleAllSections}
            disabled={groups.length === 0}
          >
            {toggleAllSectionsLabel}
          </Button>
        </div>
        <div className="fe-editor-name-row">
          {isEditingName ? (
            <div
              className={[
                'fe-editor-name-inline-wrap',
                !draftName.trim() ? 'fe-input-needs-name' : '',
              ].filter(Boolean).join(' ')}
            >
              <InlineInputWithIntegratedCheckmark
                value={draftName}
                onChange={setDraftName}
                onSave={() => {
                  commitEditName();
                }}
                onCancel={cancelEditName}
                placeholder="Form name (e.g. Level 3 Tryout)"
                inputAriaLabel="Form name"
                commitAriaLabel="Save form name"
              />
            </div>
          ) : (
            <>
              <h3 className="fe-editor-form-name-display">
                {trimmedName || 'Untitled form'}
              </h3>
              <IconButton
                aria-label="Edit form name"
                onClick={beginEditName}
                title="Edit form name"
              >
                <PencilIcon />
              </IconButton>
            </>
          )}
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="fe-empty-state">
          <p className="fe-empty-state-text">
            No groups yet. Add a group to start building your evaluation form.
          </p>
        </div>
      ) : null}

      <div className="fe-locked-group">
        <span className="fe-locked-group-label">General Comments</span>
        <span className="fe-locked-group-badge">Always included</span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={formCollisionDetection}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={groupIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="fe-groups-list">
            {groups.map((group) => (
              <FormGroupEditor
                key={group.id}
                group={group}
                onUpdate={(updated) => handleUpdateGroup(group.id, updated)}
                onDelete={() => handleDeleteGroup(group.id)}
                collapsed={collapsedGroupIds.includes(group.id)}
                onCollapsedChange={(collapsed) => handleToggleGroupCollapsed(group.id, collapsed)}
                canEnableMoreVideos={canEnableMoreVideos}
                onVideoLimitReached={() => setVideoLimitDialogOpen(true)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button
        type="button"
        variant="secondary"
        fullWidth
        className="fe-add-group-btn"
        onClick={handleAddGroup}
      >
        + Add Section
      </Button>

      <ConfirmDialog
        open={videoLimitDialogOpen}
        title="Video limit reached"
        message={`A form can only include ${MAX_VIDEOS_PER_FORM} videos. Uncheck Video on another skill before enabling it here.`}
        confirmLabel="OK"
        cancelLabel="Close"
        onConfirm={() => setVideoLimitDialogOpen(false)}
        onCancel={() => setVideoLimitDialogOpen(false)}
      />

      <div className="fe-editor-footer">
        <p className="fe-editor-stats">
          {groups.length} section{groups.length !== 1 ? 's' : ''}
          {' · '}
          {skillCount} skill{skillCount !== 1 ? 's' : ''}
        </p>
        <div className="fe-editor-actions">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {initial ? 'Save Changes' : 'Save Form'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FormEditor;
